import React, { useEffect } from "react"
import { Elements } from "@stripe/react-stripe-js"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { useState } from "react"
import Modal from "aldoo-ra/Modal"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Value from "aldoo-ra/Value"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { store } from "aldoo-ra/Store"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"

const stripePromise = getStripePromise()
let setupLoading = false

// The inner form component that uses Stripe hooks
const PaymentMethodForm = ({ onSuccess, onCancel, clearSetupIntent }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [makeDefault, setMakeDefault] = useState(false)
  const [error, setError] = useState(null)

  const isReady = stripe && ready && !loading

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (loading) return
    if (!stripe || !elements) return

    setLoading(true)
    setError(null)

    try {
      // Validate the form
      const { error: submitError } = await elements.submit()
      if (submitError) {
        throw submitError
      }

      // Confirm the setup intent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/payment-result`,
        },
      })

      if (confirmError) {
        throw confirmError
      }

      // Retrieve the setup intent details
      const { setupIntent: setupIntentDetails } =
        await stripe.retrieveSetupIntent(setupIntent.client_secret)

      if (!setupIntentDetails || !setupIntentDetails.payment_method) {
        throw new Error("Failed to retrieve payment method details")
      }

      const paymentMethodId = setupIntentDetails.payment_method

      // Attach the payment method to the user's account
      const response = await AldooStoreAPI.attachPaymentMethod({
        payment_method_id: paymentMethodId,
        makeDefault,
      })

      if (response.error) {
        throw new Error(response.error)
      }

      // Clear setupIntent immediately to prevent reuse
      clearSetupIntent?.()

      window.dispatchEvent(new CustomEvent("payment-methods-updated"))
      onSuccess?.()
    } catch (err) {
      setError(err.message)
      store.logPaymentError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-lg">
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/50 text-error rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <PaymentElement
            onReady={() => setReady(true)}
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
            }}
          />
        </div>

        <div className="mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
              className="form-checkbox h-4 w-4 text-primary"
            />
            <span className="text-sm text-neutral_3">
              Make this my default payment method
            </span>
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 rounded-md text-text bg-neutral_1/50 hover:bg-neutral_1 transition-colors duration-200"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!isReady || loading}
            className={`px-6 py-2 rounded-md text-white font-medium
              ${
                isReady && !loading
                  ? "bg-admin_primary hover:bg-admin_primary_dk"
                  : "bg-admin_neutral_3 cursor-not-allowed"
              }
              transition-colors duration-200`}
          >
            {loading ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Processing...</span>
              </span>
            ) : (
              "Add Payment Method"
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

// The main component that handles setup and modal
const AddPaymentMethod = ({ isOpen, onClose }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [setupIntent, setSetupIntent] = Value({
    key: "payment-setup-intent",
    defaultValue: null,
  })
  const { getFragment } = store

  const PreparingPayment = getFragment("checkout-preparing-payment")
  const PaymentContainer = getFragment("checkout-container")

  useEffect(() => {
    const initializeSetup = async () => {
      if (setupIntent || setupLoading) return
      setupLoading = true

      try {
        const response = await AldooStoreAPI.initializePaymentMethodSetup()
        setupLoading = false

        if (response.error) {
          console.error("Failed to initialize setup:", response.error)
          return
        }

        setSetupIntent(response)
      } catch (error) {
        console.error("Failed to initialize setup:", error)
        setupLoading = false
      }
    }

    if (isOpen) initializeSetup()
  }, [isOpen])

  if (!isOpen) return null

  return (
    <Modal
      title={setupIntent && texts?.addPaymentMethod}
      isOpen={isOpen}
      variant="centered"
      className="md:max-w-[600px] min-h-[500px]"
      onClose={onClose}
    >
      {!setupIntent && <PreparingPayment />}
      {setupIntent && setupIntent.clientSecret && (
        <PaymentContainer store={store}>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: setupIntent.clientSecret,
              loader: "always",

              appearance: {
                variables: {
                  colorDanger: "#df1b41",
                  colorBackground: "#f9f9f9",
                  colorText: "#090909",
                },
              },
            }}
          >
            <PaymentMethodForm
              clearSetupIntent={() => setSetupIntent(null)}
              onSuccess={() => {
                onClose()
                setSetupIntent(null)
              }}
              onCancel={onClose}
            />
          </Elements>
        </PaymentContainer>
      )}
    </Modal>
  )
}

export default AddPaymentMethod
